import React, { useState } from 'react'
import { useTranslation } from 'next-i18next'
import { FormikProps } from 'formik'

import { Building } from '@/types'
import { breakpoints } from '@/styles'
import useMediaQuery from '@/hooks/use-media-query'
import BuildingSearch from '@/components/building-search'
import Error from '@/components/form/error'

import { StyledGroup, StyledRow } from '../../styles/steps.styles'
import { Listing } from '../../types/Listing'
import ClearButtonComponent from './clear-button'
import MapComponent from './map'

type FieldValues = Listing & { building_id: string | null }
type Props = {
  formikProps: FormikProps<FieldValues>
}

type BuildingType = Building & {
  country: string
  lon: string
  address: string
  isNew: boolean
}

const PropertyLocationSearch = ({ formikProps }: Props) => {
  const { values, setFieldValue, errors, setValues, handleSubmit } = formikProps
  const [isBuildingSearchLoading, setIsBuildingSearchLoading] = useState(false)

  const { t } = useTranslation()
  const isMobile = useMediaQuery(`(max-width: ${breakpoints.phoneMax})`)

  const setBuildingValues = async (building: BuildingType) => {
    const {
      street_name: street_name,
      street_number: street_number,
      address: address,
      id: building_id,
      name: building_name,
      lat,
      lon,
      city,
      state,
      zip,
      isNew,
      country_code,
      ...rest
    } = building

    const buildingValues = {
      building,
      address,
      street_name,
      street_number: street_number || '',
      building_id: building_id || null,
      building_name,
      lat,
      lon,
      state: isNew ? '' : state,
      city: city || '',
      zip: zip || '',
      ...rest,
      country: country_code?.toLowerCase()
    }

    await Object.entries(buildingValues).forEach(([key, value]) =>
      setFieldValue(key, value)
    )

    handleSubmit()
  }

  const handleClear = () => {
    setValues({
      ...values,
      country: '',
      suite: '',
      building_name: '',
      city: '',
      state: '',
      zip: '',
      street_name: '',
      building: '',
      building_id: null
    })
  }

  const handleEnterNewAddess = (streetName: string) => {
    setFieldValue('street_name', streetName)
  }

  return (
    <>
      <StyledRow>
        <StyledGroup col={12}>
          <BuildingSearch
            name="street_name"
            onChange={setBuildingValues}
            isLoading={isBuildingSearchLoading}
            setIsLoading={setIsBuildingSearchLoading}
            handleClear={handleClear}
            newAddressOnClick={handleEnterNewAddess}
            value={values.building}
            buildingType={values.type}
            wrapBuildingName={isMobile}
            countryCode={['ca', 'us']}
            placeholder={t('common.street_address', 'Street address')}
            required
            hasError={!!errors['street_name']}
            ClearButtonComponent={ClearButtonComponent}
            clearButton
            showInputText={false}
          />
          <Error name={'street_name'} />
        </StyledGroup>
      </StyledRow>
      <MapComponent building={values?.building} />
    </>
  )
}

export default PropertyLocationSearch
